import { format, parseISO } from "date-fns";
import type { ConversationEvent } from "../types";
import { MarkdownMessage } from "./MarkdownMessage";

interface MessageCardProps {
  event: ConversationEvent;
  onFilterByUuid?: (uuid: string) => void;
  onFilterBySession?: (sessionId: string) => void;
  onFilterByRepo?: (repoUrl: string) => void;
  onFilterByFile?: (fileName: string) => void;
  truncateLength?: number;
  userAvatar?: string | null;
}

const getTypeColor = (type: string) => {
  switch (type?.toLowerCase()) {
    case "user":
      return "bg-blue-600";
    case "assistant":
      return "bg-purple-600";
    case "tool_use":
      return "bg-yellow-600";
    case "tool_result":
      return "bg-green-600";
    default:
      return "bg-gray-600";
  }
};

const extractRepoName = (url: string | null) => {
  if (!url) return "local";
  const match = url.match(/\/([^/]+)\.git$/);
  return match ? match[1] : url;
};

const truncateFromStart = (str: string, maxLength = 30) => {
  if (str.length <= maxLength) return str;
  return "..." + str.slice(-(maxLength - 3));
};

const formatMessage = (message: string | null, truncateLength?: number) => {
  if (!message) return "No content";
  if (truncateLength && message.length > truncateLength) {
    return message.slice(0, truncateLength) + "...";
  }
  return message;
};

// Parse message and return segments for rendering
// Handles IDE notification tags like <ide_opened_file>, <ide_selection>, etc.
interface MessageSegment {
  type: "text" | "ide-notification";
  content: string;
  tagName?: string;
}

const parseMessageSegments = (message: string): MessageSegment[] => {
  const segments: MessageSegment[] = [];
  let lastIndex = 0;

  const regex =
    /<(ide_opened_file|ide_selection|ide_diagnostics|system-reminder)>([\s\S]*?)<\/\1>/g;
  let match;

  while ((match = regex.exec(message)) !== null) {
    // Add text before this tag
    if (match.index > lastIndex) {
      const textBefore = message.slice(lastIndex, match.index).trim();
      if (textBefore) {
        segments.push({ type: "text", content: textBefore });
      }
    }

    // Add the IDE notification
    segments.push({
      type: "ide-notification",
      content: match[2].trim(),
      tagName: match[1],
    });

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  if (lastIndex < message.length) {
    const remaining = message.slice(lastIndex).trim();
    if (remaining) {
      segments.push({ type: "text", content: remaining });
    }
  }

  return segments;
};

const getTypeBadgeLabel = (event: ConversationEvent) => {
  if (event.event_type === "user") {
    return event.user_name || "user";
  }
  return event.event_type || "unknown";
};

function FormattedUserMessage({
  message,
  truncateLength,
}: {
  message: string | null;
  truncateLength?: number;
}) {
  if (!message) return <span>No content</span>;

  const segments = parseMessageSegments(message);

  // If it's a simple message with no IDE tags, just format and return
  if (segments.length === 1 && segments[0].type === "text") {
    return <>{formatMessage(message, truncateLength)}</>;
  }

  return (
    <>
      {segments.map((segment, i) => {
        if (segment.type === "ide-notification") {
          return (
            <span key={i} className="italic text-gray-500 block">
              {segment.content}
            </span>
          );
        }
        return <span key={i}>{segment.content}</span>;
      })}
    </>
  );
}

export function MessageCard({
  event,
  onFilterByUuid,
  onFilterBySession,
  onFilterByRepo,
  onFilterByFile,
  truncateLength,
  userAvatar,
}: MessageCardProps) {
  const isAssistant = event.event_type === "assistant";
  const isUser = event.event_type === "user";
  const hasInteractiveFilters =
    onFilterByUuid || onFilterBySession || onFilterByRepo || onFilterByFile;

  const anchorId = event.event_uuid ? `msg-${event.event_uuid}` : undefined;

  const highlighted = !!anchorId && window.location.hash === `#${anchorId}`;

  const handleAnchorClick = () => {
    if (!anchorId) return;
    window.location.hash = anchorId;
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${anchorId}`;
    navigator.clipboard.writeText(url).catch(() => {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    });
  };

  return (
    <div
      id={anchorId}
      className={[
        "group relative",
        highlighted ? "bg-purple-900/30 border-purple-500" : "",
        isAssistant
          ? "p-4 border border-gray-700/50 rounded-lg"
          : "bg-gray-800 rounded-lg p-4 border border-gray-700",
      ].join(" ")}
    >
      {anchorId && (
        <button
          onClick={handleAnchorClick}
          className="absolute -left-6 top-3 opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-purple-400"
          title="Copy link to this message"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
      )}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-3">
          {isUser && userAvatar ? (
            <img src={userAvatar} alt="User" className="w-6 h-6 rounded-full" />
          ) : (
            !isAssistant && (
              <span
                className={`px-2 py-1 rounded text-xs font-medium text-white ${getTypeColor(
                  event.event_type,
                )}`}
              >
                {getTypeBadgeLabel(event)}
              </span>
            )
          )}
          {event.event_uuid && onFilterByUuid ? (
            <button
              onClick={() => onFilterByUuid(event.event_uuid!)}
              className="text-sm text-gray-400 hover:text-purple-400 transition-colors cursor-pointer"
              title="Link to this message"
            >
              {format(parseISO(event.event_timestamp!), "MMM d, yyyy h:mm a")}
            </button>
          ) : (
            <span className="text-sm text-gray-400">
              {format(parseISO(event.event_timestamp!), "MMM d, yyyy h:mm a")}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          {event.git_remote_url && onFilterByRepo ? (
            <button
              onClick={() => onFilterByRepo(event.git_remote_url!)}
              className="bg-gray-700 px-2 py-1 rounded hover:bg-gray-600 transition-colors cursor-pointer"
              title="Filter by this repository"
            >
              {extractRepoName(event.git_remote_url)}
            </button>
          ) : event.git_remote_url && !hasInteractiveFilters ? (
            <span className="bg-gray-700 px-2 py-1 rounded">
              {extractRepoName(event.git_remote_url)}
            </span>
          ) : null}
          {event.event_session_id && onFilterBySession ? (
            <button
              onClick={() => onFilterBySession(event.event_session_id)}
              className="font-mono hover:text-purple-400 transition-colors cursor-pointer"
              title="Filter by this session"
            >
              §{event.event_session_id.slice(0, 8)}
            </button>
          ) : event.event_session_id ? (
            <span className="font-mono">
              §{event.event_session_id.slice(0, 8)}
            </span>
          ) : null}
          {event.file_name && onFilterByFile ? (
            <button
              onClick={() => onFilterByFile(event.file_name)}
              className="font-mono hover:text-purple-400 transition-colors cursor-pointer"
              title={`${event.file_name}:${event.line_number}`}
            >
              {truncateFromStart(`${event.file_name}:${event.line_number}`, 35)}
            </button>
          ) : null}
        </div>
      </div>

      {isAssistant ? (
        <div className="text-gray-300">
          <MarkdownMessage
            content={formatMessage(event.event_message, truncateLength) || ""}
          />
        </div>
      ) : (
        <div className="text-gray-300 whitespace-pre-wrap break-words font-mono text-sm">
          <FormattedUserMessage
            message={event.event_message}
            truncateLength={truncateLength}
          />
        </div>
      )}
    </div>
  );
}
